import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// 动态加载脚本的函数
function loadScript(url: string) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = 'avatar-anime-script';
    script.src = url;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

// 按顺序加载所需的脚本
async function loadDependencies(url: string) {
  const scripts = [url];

  for (const script of scripts) {
    await loadScript(script);
  }
}

export const DigitalHuman2D = ({
  className,
  avatarType = 'sparticle',
  animeModuleLoaded = true,
}: {
  className?: string;
  avatarType?: string;
  animeModuleLoaded?: boolean;
}) => {
  const { id } = useParams();
  const isDev = ['development', 'pre'].includes(import.meta.env.VITE_STAGE);
  const [isSparticleAvatar, setIsSparticleAvatar] = useState<boolean>(false);
  const [imgOpacity, setImgOpacity] = useState(1);
  const [canvasOpacity, setCanvasOpacity] = useState(1);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  const urlList = {
    normal: 'https://static.yangth25.workers.dev/haru/anime_haru.js',

    dnpAvatar: 'https://static.yangth25.workers.dev/dnp/anime_nekomata.js',

    noCrossedArms: 'https://cdn.jsdelivr.net/gh/mikeyang01/static_hosting@master/no_crossed_arms/anime_avatar.min.js ',

    sparticle: isDev
      ? 'https://static.yangth25.workers.dev/spa_yumi/local_anime_dev.js'
      : 'https://static.yangth25.workers.dev/spa_yumi/local_anime.js',
  };

  useEffect(() => {
    // 5d82886e-a515-489e-a6ad-ad5099864a64/6e96a3a8-3a29-48dd-8ef0-d2bc6744863c 为 DNP 正式环境 bot id
    // 28b48307-4701-4cf1-872a-cdadbafa6361 为测试bot id
    const isDnpAvatar =
      id &&
      [
        '5d82886e-a515-489e-a6ad-ad5099864a64',
        '28b48307-4701-4cf1-872a-cdadbafa6361',
        '6e96a3a8-3a29-48dd-8ef0-d2bc6744863c',
      ].includes(id);
    setIsSparticleAvatar(avatarType === 'sparticle' && !isDnpAvatar);
    const url = isDnpAvatar ? urlList.dnpAvatar : urlList[avatarType as keyof typeof urlList] || 'sparticle';
    loadDependencies(url).catch((error) => {
      console.error('Failed to load dependencies:', error);
    });

    return () => {
      const script = document.getElementById('avatar-anime-script');
      script?.remove();
      window.removeEventListener('avatarInitialized', () => {});
    };
  }, []);

  useEffect(() => {
    if (!isSparticleAvatar) return;
    if (animeModuleLoaded) {
      setImgOpacity(0);
      setCanvasOpacity(1);
    } else {
      setImgOpacity(1);
      setCanvasOpacity(0);
    }
  }, [animeModuleLoaded, isSparticleAvatar]);

  return (
    <div className={`relative w-full h-full ${className || ''}`} style={{ width: '100%', height: '100%' }}>
      {/* 骨骼图层 */}
      {isSparticleAvatar && (
        <img
          src="/skeleton_line.png"
          className="absolute left-1/2"
          style={{
            maxHeight: '100%',
            maxWidth: '100%',
            top: '24%',
            transform: `translateX(-50%) scale(1.6)`,
            objectFit: 'contain',
            userSelect: 'none',
            pointerEvents: 'none',
            zIndex: 10,
            opacity: imgOpacity,
            transition: 'opacity 0.8s cubic-bezier(0.4,0,0.2,1)',
            animation: imgOpacity > 0 ? 'flicker 2s ease-in-out infinite' : 'none',
          }}
        />
      )}
      {/* 数字人canvas层 */}
      <canvas
        id="canvas"
        className="w-full h-full"
        style={{
          opacity: canvasOpacity,
          transition: 'opacity 0.8s cubic-bezier(0.4,0,0.2,1)',
          zIndex: 1,
        }}
      />
      {/* 动画样式 */}
      <style>{`
        @keyframes flicker {
          0%, 100% {
            opacity: 0.6;
          }
          50% {
            opacity: 0.3;
          }
        }
      `}</style>
    </div>
  );
};
